/**
 * PacMassive Game Configuration
 *
 * This file contains all configurable constants for the game.
 * Centralizing these values makes it easier to tune gameplay and visuals.
 */

// Gameplay constants
export const GAME_CONFIG = {
  // Round timing
  ROUND_DURATION_MS: 5 * 60 * 1000, // 5 minutes in milliseconds

  // Maze configuration
  MAZE: {
    MIN_SIZE: 16,
    MAX_SIZE: 50,
    DEFAULT_SIZE: 20,
    PLAYERS_PER_SIZE_INCREASE: 1, // Add 1 to maze size for every N players
  },

  // Player settings
  PLAYER: {
    MOVE_SPEED: 0.06, // Adjusted to be closer to ghost speed (0.045)
    RADIUS: 0.4, // Hitbox/visual radius
    RESPAWN_TIME: 3000, // ms
    DOT_COLLECTION_POINTS: 10,
  },

  // Ghost settings
  GHOST: {
    MIN_COUNT: 4, // Increased from 3 for more challenge
    BASE_SPEED: 0.045, // Adjusted for better gameplay balance
    SPEED_VARIATION: 0.01, // Reduced variation for more consistent experience
    DETECTION_RADIUS: 6.0, // Increased for better player detection
    COLLISION_RADIUS: 0.5,
    RELEASE_INTERVAL: 5000, // Time between ghost releases from jail
    BEHAVIOR_CHANGE_MIN: 7000, // Min time between behavior changes
    BEHAVIOR_CHANGE_MAX: 15000, // Max time between behavior changes
  },

  // Items
  ROCKET: {
    SPAWN_PERCENTAGE: 0.05, // 5% of cells will have rockets
    COOLDOWN: 10000, // 10 seconds between rocket uses
  },

  // Anti-cheat system
  ANTI_CHEAT: {
    VIOLATION_THRESHOLD: 3,
    VIOLATION_RESET_TIME: 10000, // ms
    MAX_MOVE_DISTANCE: 0.08, // Reduced from 0.25 to detect speed hacks better
    TELEPORT_THRESHOLD: 1.0,
  },
};

// Visual/rendering settings
export const VISUAL_CONFIG = {
  // Camera settings
  CAMERA: {
    FOV: 50,
    NEAR: 0.1,
    FAR: 1000,
    HEIGHT_FACTOR: 1.8, // Controls how high the camera is positioned
    DISTANCE_FACTOR: 1.5, // Controls how far back the camera is positioned
    POSITION_Y: 15, // Base height
    POSITION_Z_OFFSET: 3, // Base Z offset
  },

  // Object sizes and positions
  OBJECT: {
    PLAYER_HEIGHT: 0.4,
    WALL_HEIGHT: 0.8,
    WALL_THICKNESS: 0.08, // Reduced from 0.1 for wider pathways
    DOT_RADIUS: 0.1,
    FLOOR_Y: 0,
  },

  // Colors (in hex format)
  COLORS: {
    BACKGROUND: 0x000000,
    FLOOR: 0x000044,
    WALL: 0x0088ff,
    WALL_EMISSIVE: 0x0033aa,
    PLAYER: 0xffff00,
    PLAYER_EMISSIVE: 0x332200,
    TEAM_A: 0xff0000, // Red
    TEAM_A_EMISSIVE: 0x330000,
    TEAM_B: 0x0000ff, // Blue
    TEAM_B_EMISSIVE: 0x000033,
    DOT: 0xffffff,
    DOT_EMISSIVE: 0x333333,
    ROCKET: 0xff0000,
    ROCKET_EMISSIVE: 0x330000,
    GHOST_COLORS: [
      { color: 0xff0000, emissive: 0x330000 }, // Red
      { color: 0xffb8ff, emissive: 0x330033 }, // Pink
      { color: 0x00ffff, emissive: 0x003333 }, // Cyan
      { color: 0xffb852, emissive: 0x332200 }, // Orange
      { color: 0x00ff00, emissive: 0x003300 }, // Green
      { color: 0xb000ff, emissive: 0x300033 }, // Purple
    ],
  },

  // Animation timings
  ANIMATION: {
    RESPAWN_PULSE_SPEED: 200, // ms per pulse cycle
    ROCKET_PULSE_SPEED: 0.003, // Rotation speed for pickups
    GHOST_BOB_SPEED: 0.005, // Ghost hovering animation speed
    GHOST_BOB_HEIGHT: 0.1, // Ghost hovering height
  },

  // UI settings
  UI: {
    NOTIFICATION_DURATION: {
      INFO: 5000,
      WARNING: 4000,
      GLOBAL: 3000,
    },
  },
};
